// Admin › Warehouse › Reports › Queued exports (BE services/report-jobs.js,
// migration 272). The Jobalots import files asked for from Admin › Wipe and
// Admin › Manual Products ("Queue Jobalots export") are built by the jobs
// runner — the multi-language title column comes from the translation cache
// and Claude — and land here. Lists the last 7 days' jobs, newest first,
// polls while one is still queued or building, and downloads the ready ones.
// A job whose file shipped with blank translation cells says how many rows
// were left blank (BE decision 2: never hold the file back).
import { useCallback, useEffect, useState } from "react";
import { Download, ListTodo } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";

export interface ReportJob {
    id: number;
    report_id: string;
    warehouse: string | null;
    params: Record<string, string> | null;
    requested_by: string;
    status: "queued" | "building" | "ready" | "failed";
    attempts: number;
    row_count: number | null;
    failed_translations: number | null;
    file_name: string | null;
    byte_size: number | null;
    error: string | null;
    created_at: string;
    started_at: string | null;
    built_at: string | null;
}

const LONDON = "Europe/London";
// How often the list is re-read while a job is still queued or building.
export const POLL_MS = 10_000;

const timeOf = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-GB", { timeZone: LONDON, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

const sizeOf = (bytes: number | null) => {
    if (bytes == null) return "";
    return bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// The filters a job was queued with, as "key: value" pairs, for the hover.
export const describeParams = (params: ReportJob["params"]) =>
    Object.entries(params ?? {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}: ${v}`).join(", ");

const ReportsQueued = ({ pollMs = POLL_MS }: { pollMs?: number }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<ReportJob[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const load = useCallback(async (): Promise<ReportJob[] | null> => {
        try {
            const res = await apiFetch(`/reports/jobs`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const jobs: ReportJob[] = Array.isArray(data?.jobs) ? data.jobs : [];
            setRows(jobs);
            return jobs;
        } catch {
            toast.error(t("reports_queued_load_failed"), { toastId: "reports-queued-load" });
            return null;
        } finally {
            setLoaded(true);
        }
    }, [t]);

    // First load, then poll only while something is still in flight.
    useEffect(() => {
        let alive = true;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const tick = async () => {
            const jobs = await load();
            if (!alive) return;
            const busy = (jobs ?? []).some(j => j.status === "queued" || j.status === "building");
            if (busy) timer = setTimeout(tick, pollMs);
        };
        tick();
        return () => { alive = false; if (timer) clearTimeout(timer); };
    }, [load, pollMs]);

    const download = async (row: ReportJob) => {
        setDownloadingId(row.id);
        try {
            const res = await apiFetch(`/reports/jobs/${row.id}/download`, {});
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.message || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = row.file_name || `${row.report_id}-${row.id}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(`${t("reports_download_failed")}${e instanceof Error && e.message ? ` (${e.message})` : ""}`, { toastId: "reports-queued-download" });
        } finally {
            setDownloadingId(null);
        }
    };

    const reportLabel = (id: string) => {
        const key = `reports_queued_report_${id.replace(/-/g, "_")}`;
        const label = t(key);
        return label === key ? id : label;
    };
    const statusLabel = (s: ReportJob["status"]) =>
        s === "ready" ? t("reports_nightly_ready")
            : s === "building" ? t("reports_nightly_building")
                : s === "queued" ? t("reports_queued_status_queued")
                    : t("reports_nightly_failed");

    return (
        <section className="reports-group reports-nightly reports-queued">
            <h3 className="reports-group-title"><ListTodo size={14} className="reports-nightly-icon" />{t("reports_queued_title")}</h3>
            <p className="reports-nightly-hint">{t("reports_queued_hint")}</p>
            {loaded && rows.length === 0 && <p className="reports-empty">{t("reports_queued_none")}</p>}
            {rows.length > 0 && (
                <div className="locations-table-wrap">
                    <table className="locations-table reports-nightly-table">
                        <thead>
                            <tr>
                                <th>{t("reports_queued_col_report")}</th>
                                <th>{t("reports_queued_col_warehouse")}</th>
                                <th>{t("reports_queued_col_requested_by")}</th>
                                <th>{t("reports_queued_col_requested")}</th>
                                <th>{t("reports_nightly_col_lines")}</th>
                                <th>{t("reports_nightly_col_size")}</th>
                                <th>{t("reports_nightly_col_status")}</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.id}>
                                    <td title={describeParams(row.params) || undefined}>{reportLabel(row.report_id)}</td>
                                    <td>{row.warehouse || t("reports_queued_all_warehouses")}</td>
                                    <td>{row.requested_by}</td>
                                    <td>{timeOf(row.created_at)}</td>
                                    <td>{row.row_count?.toLocaleString("en-GB") ?? ""}</td>
                                    <td>{sizeOf(row.byte_size)}</td>
                                    <td className={`reports-nightly-status reports-nightly-status--${row.status}`} title={row.status === "failed" && row.error ? row.error : undefined}>
                                        {statusLabel(row.status)}
                                    </td>
                                    <td>
                                        {row.status === "ready" && (
                                            <div className="reports-queued-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={() => download(row)}
                                                    disabled={downloadingId !== null}
                                                    aria-label={`${t("reports_download")} ${row.file_name ?? row.id}`}
                                                >
                                                    <Download size={14} />
                                                    {downloadingId === row.id ? t("reports_nightly_downloading") : t("reports_download")}
                                                </button>
                                                {(row.failed_translations ?? 0) > 0 && (
                                                    <span className="reports-queued-warning" role="alert">
                                                        {t("reports_queued_untranslated", { count: String(row.failed_translations) })}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};

export default ReportsQueued;
